import { DestroyRef, Injectable, inject, signal } from '@angular/core';

const LEFT_WIDTH_KEY = 'wb-editor-left-width';
const RIGHT_WIDTH_KEY = 'wb-editor-right-width';
const TIMELINE_MIN_HEIGHT_KEY = 'wb-editor-timeline-min-height';
const TIMELINE_MIN_HEIGHT_FLOOR = 170;

function readStoredNumber(key: string, fallback: number): number {
	const saved = localStorage.getItem(key);
	const parsed = saved ? Number(saved) : NaN;
	return Number.isFinite(parsed) ? parsed : fallback;
}

/**
 * Left/right/timeline panel sizing for the editor's resizable layout.
 * Left/right are fixed widths. The timeline instead gets a *minimum* height
 * (`timelineMinHeight`) — by default it just claims whatever's left below
 * the player (see DEV-57), but dragging the bottom handle raises that floor
 * so the timeline can be pinned taller (DEV-64). All three persist to
 * localStorage on drag end (mirrors `ThemeService`'s `wb-theme` pattern).
 */
@Injectable()
export class EditorLayoutService {
	readonly leftWidth = signal(readStoredNumber(LEFT_WIDTH_KEY, 280));
	readonly rightWidth = signal(readStoredNumber(RIGHT_WIDTH_KEY, 460));
	readonly timelineMinHeight = signal(Math.max(TIMELINE_MIN_HEIGHT_FLOOR, readStoredNumber(TIMELINE_MIN_HEIGHT_KEY, TIMELINE_MIN_HEIGHT_FLOOR)));

	private dragSide: 'left' | 'right' | 'bottom' | null = null;
	private dragStartX = 0;
	private dragStartY = 0;
	private dragStartWidth = 0;
	private dragStartHeight = 0;

	constructor() {
		inject(DestroyRef).onDestroy(() => this.cancelDrag());
	}

	startResize(side: 'left' | 'right' | 'bottom', event: MouseEvent): void {
		this.dragSide = side;
		event.preventDefault();
		document.body.style.userSelect = 'none';
		if (side === 'bottom') {
			this.dragStartY = event.clientY;
			this.dragStartHeight = this.timelineMinHeight();
			document.body.style.cursor = 'row-resize';
		} else if (side === 'left') {
			this.dragStartX = event.clientX;
			this.dragStartWidth = this.leftWidth();
			document.body.style.cursor = 'col-resize';
		} else {
			this.dragStartX = event.clientX;
			this.dragStartWidth = this.rightWidth();
			document.body.style.cursor = 'col-resize';
		}
	}

	onMouseMove(event: MouseEvent): void {
		if (!this.dragSide) return;
		if (this.dragSide === 'bottom') {
			const delta = this.dragStartY - event.clientY;
			this.timelineMinHeight.set(Math.max(TIMELINE_MIN_HEIGHT_FLOOR, Math.min(600, this.dragStartHeight + delta)));
		} else if (this.dragSide === 'left') {
			const delta = event.clientX - this.dragStartX;
			this.leftWidth.set(Math.max(244, Math.min(500, this.dragStartWidth + delta)));
		} else {
			const delta = event.clientX - this.dragStartX;
			this.rightWidth.set(Math.max(320, Math.min(900, this.dragStartWidth - delta)));
		}
	}

	onMouseUp(): void {
		if (!this.dragSide) return;
		const side = this.dragSide;
		this.dragSide = null;
		document.body.style.cursor = '';
		document.body.style.userSelect = '';
		if (side === 'left') localStorage.setItem(LEFT_WIDTH_KEY, String(this.leftWidth()));
		else if (side === 'right') localStorage.setItem(RIGHT_WIDTH_KEY, String(this.rightWidth()));
		else localStorage.setItem(TIMELINE_MIN_HEIGHT_KEY, String(this.timelineMinHeight()));
	}

	private cancelDrag(): void {
		this.dragSide = null;
		document.body.style.cursor = '';
		document.body.style.userSelect = '';
	}
}
